import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { useTranslation } from "react-i18next";
import { dateDiff } from "~/helpers/date";
import { WorkflowListSteps } from "~/molecules/WorkflowListSteps";
import { Link } from "react-router";
import {
  hasWorkflowData,
  type DocumentReleaseSummaryWithWorkflow,
} from "~/api/types";
import SimpleDataTable, { type TableColumn } from "~/molecules/SimpleDataTable";
import { DueDateCell } from "~/molecules/DueDateCell";
import { useDocumentReleases, QueryParams } from "~/api/v2/useDocumentReleases";
import { DocumentArtifactStatus } from "~/config/types";
import { CenterSpinner } from "~/atoms/spinner";
import NoResult from "~/molecules/NoResult";
import { FilterPopover } from "~/molecules/FilterPopover";
import { SelectActiveFilterChips } from "~/organisms/filters/SelectActiveFilterChips";
import SwitchWithLabel from "~/molecules/SwitchWithLabel";
import SearchBar from "~/molecules/SearchBar";
import { useFilter, type Filter } from "~/hooks/useFilter";
import { useTags } from "~/api/swr/useTags";
import { useTiers } from "~/api/swr/useTiers";
import { useCategories } from "~/api/swr/useCategories";
import { extractOwners, progressRatio } from "~/helpers/workflow";
import DocumentIcon from "~/molecules/DocumentIcon";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { featureFlags } from "~/config/featureFlags";
import { useUserContext } from "~/hooks/useUserContext";
import { tabs } from "~/pages/WorkflowsPage";
import { WorkflowProgress } from "~/molecules/WorkflowProgress";
import { MyWorkflowStepsCell } from "~/molecules/MyWorkflowStepsCell";
import {
  getNoTierLabel,
  getTierTitle,
  mapTierOptions,
  tierIdToFormValue,
} from "~/helpers/tier";
import { getCategoryTitle } from "~/helpers/category";
import { Toolbar } from "~/molecules/Toolbar";

export const WorkflowList = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const {
    data: allReleases,
    isLoading,
    mutate,
  } = useDocumentReleases({
    statuses: [DocumentArtifactStatus.draft],
    includeWorkflowData: true,
    titleSearch: searchParams.get(QueryParams.Title),
  });
  const activeTab = searchParams.get("tab") ?? tabs.ongoing;

  const releases = allReleases
    ?.filter(hasWorkflowData)
    .filter((release) =>
      activeTab === tabs.ongoing
        ? release.artifact.workflow.isOngoing
        : !release.artifact.workflow.isOngoing,
    );

  const { data: tags } = useTags();
  const { data: tiers } = useTiers();
  const { data: categories } = useCategories();
  const { t } = useTranslation();
  const { user } = useUserContext();
  const isRefNumEnabled = useFeatureFlagEnabled(featureFlags.referenceNumber);

  const [expandAll, setExpandAll] = useState(false);

  const filterInput: Filter<DocumentReleaseSummaryWithWorkflow>[] =
    categories && tiers && releases && tags
      ? [
          {
            id: "tier",
            label: t(($) => $.commons.document.tier),
            isMulti: true,
            options: mapTierOptions(tiers, t) ?? [],
            getValue: (release) => tierIdToFormValue(release.document.tier?.id),
          },
          {
            id: "category",
            label: t(($) => $.commons.document.category),
            isMulti: true,
            options: categories.map((i) => ({
              label: i.title,
              value: i.id.toString(),
            })),
            getValue: (release) =>
              release.document.category
                ? `${release.document.category.id}`
                : [],
          },
          {
            id: "tag",
            label: t(($) => $.commons.document.tag),
            isMulti: true,
            options: tags.map((i) => ({
              label: i.title,
              value: i.id.toString(),
            })),
            getValue: (release) => release.document.tagIds.map((id) => `${id}`),
          },
          {
            id: "owner",
            label: t(($) => $.commons.document.owner),
            isMulti: true,
            options: extractOwners(
              releases.map((release) => release.document.owner),
              user.id,
            ),
            getValue: (release) => `${release.document.owner.userId}`,
          },
        ]
      : [];

  const {
    filters,
    activeFilters,
    handleClearFilters,
    handleSelectFilter,
    filterItems,
  } = useFilter(filterInput);

  const filteredReleases = useMemo(
    () => filterItems(releases ?? []),
    [releases, filterItems],
  );

  if (isLoading) {
    return <CenterSpinner />;
  }

  if (!allReleases?.length && !searchParams.get(QueryParams.Title)) {
    return <NoResult />;
  }

  const allColumns: TableColumn<DocumentReleaseSummaryWithWorkflow>[] = [
    ...(isRefNumEnabled
      ? [
          {
            columnKey: "commons.document.referenceNumber",
            header: t(($) => $.commons.document.referenceNumber),
            compareFn: (
              a: DocumentReleaseSummaryWithWorkflow,
              b: DocumentReleaseSummaryWithWorkflow,
            ) =>
              (a.document.referenceNumber ?? "").localeCompare(
                b.document.referenceNumber ?? "",
              ),
            cell: (i: DocumentReleaseSummaryWithWorkflow) => (
              <span>{i.document.referenceNumber ?? "-"}</span>
            ),
          },
        ]
      : []),
    {
      columnKey: "commons.title",
      header: t(($) => $.commons.title),
      compareFn: (a, b) => a.artifact.title.localeCompare(b.artifact.title),
      cell: (i) => (
        <Link
          className="flex items-center gap-1 hover:underline"
          to={`/documents/${i.document.id}?releaseId=${i.id}&tab=workflow`}
        >
          <DocumentIcon
            type={i.artifact.fileType}
            className="size-5 shrink-0"
          />
          {i.artifact.title}
        </Link>
      ),
    },
    {
      columnKey: "commons.workflow.mySteps",
      header: t(($) => $.commons.workflow.mySteps),
      compareFn: (a, b) => {
        const aHasParticipant = a.artifact.workflow.steps.some((step) =>
          step.users.some((stepUser) => stepUser.userId === user.id),
        );
        const bHasParticipant = b.artifact.workflow.steps.some((step) =>
          step.users.some((stepUser) => stepUser.userId === user.id),
        );

        if (aHasParticipant === bHasParticipant) return 0;
        return aHasParticipant ? -1 : 1;
      },
      cell: (release) => (
        <MyWorkflowStepsCell
          steps={release.artifact.workflow.steps}
          currentUserId={user.id}
        />
      ),
    },
    {
      columnKey: "commons.workflow.progress",
      header: t(($) => $.commons.workflow.progress),
      compareFn: (a, b) =>
        progressRatio(a.artifact.workflow) - progressRatio(b.artifact.workflow),
      cell: (release) => (
        <WorkflowProgress workflow={release.artifact.workflow} />
      ),
    },
    {
      columnKey: "commons.document.reviewFrom",
      header: t(($) => $.commons.document.reviewFrom),
      compareFn: (a, b) =>
        dateDiff(a.artifact.reviewFrom, b.artifact.reviewFrom),
      cell: (release) => <DueDateCell dueDate={release.artifact.reviewFrom} />,
    },
    {
      columnKey: "commons.document.reviewTo",
      header: t(($) => $.commons.document.reviewTo),
      compareFn: (a, b) => dateDiff(a.artifact.reviewTo, b.artifact.reviewTo),
      cell: (release) => <DueDateCell dueDate={release.artifact.reviewTo} />,
    },
    {
      columnKey: "commons.document.owner",
      header: t(($) => $.commons.document.owner),
      compareFn: (a, b) =>
        a.document.owner.firstName.localeCompare(b.document.owner.firstName),
      cell: (release) => (
        <span>
          {release.document.owner.firstName} {release.document.owner.lastName}
        </span>
      ),
    },
    {
      columnKey: "commons.document.category",
      header: t(($) => $.commons.document.category),
      compareFn: (a, b) =>
        getCategoryTitle(a.document.category?.title, t).localeCompare(
          getCategoryTitle(b.document.category?.title, t),
        ),
      cell: (release) => (
        <span>{getCategoryTitle(release.document.category?.title, t)}</span>
      ),
    },
    {
      columnKey: "commons.document.tier",
      header: t(($) => $.commons.document.tier),
      compareFn: (a, b) =>
        getTierTitle(a.document.tier?.title, t).localeCompare(
          getTierTitle(b.document.tier?.title, t),
        ),
      cell: (release) =>
        release.document.tier ? (
          release.document.tier.title
        ) : (
          <span className="text-muted-foreground">{getNoTierLabel(t)}</span>
        ),
    },
  ];

  const columns = (
    activeTab === tabs.ongoing
      ? allColumns.filter((c) => c.columnKey !== "commons.document.reviewFrom")
      : allColumns.filter((c) => c.columnKey !== "commons.workflow.progress")
  ).map((c) => ({
    ...c,
    defaultSort: c.columnKey === "commons.workflow.mySteps",
  }));

  const renderData = () => {
    if (!filteredReleases?.length) {
      return <NoResult />;
    }

    return (
      <SimpleDataTable
        columns={columns}
        data={filteredReleases}
        renderExpandedRow={(release) => (
          <WorkflowListSteps release={release} onChange={() => mutate()} />
        )}
        enablePinning={true}
        expandAll={expandAll}
        storageKey="workflows"
        wrapperClassName="border-border max-h-[calc(100vh-14rem)] border"
        headerClassName="bg-muted"
      />
    );
  };

  const setTitleSearch = (value: string) => {
    const params = new URLSearchParams(searchParams);
    if (value) {
      params.set(QueryParams.Title, value);
    } else {
      params.delete(QueryParams.Title);
    }
    setSearchParams(params);
  };

  return (
    <div className="space-y-4">
      <Toolbar
        left={
          <>
            <FilterPopover
              filters={filters}
              activeFiltersCount={activeFilters.length}
              onClearFilters={handleClearFilters}
              onSelectFilter={handleSelectFilter}
            />
            <SwitchWithLabel
              label={t(($) => $.commons.buttons.expandAll)}
              checked={expandAll}
              onCheckedChange={setExpandAll}
            />
            <SearchBar
              placeholder={t(($) => $.commons.workflows.search)}
              value={searchParams.get(QueryParams.Title) ?? ""}
              onDebouncedSearch={setTitleSearch}
              onSearch={setTitleSearch}
              onClearSearch={() => setTitleSearch("")}
            />
          </>
        }
      />
      <SelectActiveFilterChips
        activeFilters={activeFilters}
        onSelectFilter={handleSelectFilter}
      />
      {renderData()}
    </div>
  );
};
